import React, { useState, useEffect, useReducer } from "react";
import {
  Button, IconButton, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, Chip, Tooltip, Switch,
} from "@mui/material";
import { Edit, DeleteOutline, Add } from "@mui/icons-material";
import { toast } from "react-toastify";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import KeywordRuleModal from "../../components/KeywordRuleModal";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const MATCH_LABELS = { exact: "Exato", contains: "Contém", startsWith: "Começa com" };

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD":
      return action.payload;
    case "UPDATE": {
      const idx = state.findIndex(r => r.id === action.payload.id);
      if (idx !== -1) { const s = [...state]; s[idx] = action.payload; return s; }
      return [action.payload, ...state];
    }
    case "DELETE":
      return state.filter(r => r.id !== action.payload);
    default:
      return state;
  }
};

const KeywordRules = () => {
  const [rules, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    api.get("/keyword-rules").then(({ data }) => {
      dispatch({ type: "LOAD", payload: data });
      setLoading(false);
    }).catch(toastError);
  }, []);

  const handleEdit = (rule) => { setSelected(rule); setModalOpen(true); };
  const handleNew = () => { setSelected(null); setModalOpen(true); };

  const handleToggle = async (rule) => {
    try {
      const { data } = await api.put(`/keyword-rules/${rule.id}`, { ...rule, isActive: !rule.isActive });
      dispatch({ type: "UPDATE", payload: data });
    } catch (err) { toastError(err); }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/keyword-rules/${deleteId}`);
      dispatch({ type: "DELETE", payload: deleteId });
      toast.success("Regra removida.");
    } catch (err) { toastError(err); }
  };

  return (
    <MainContainer>
      <ConfirmationModal
        title="Excluir regra?"
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleDelete}
      >
        A regra de palavra-chave será removida permanentemente.
      </ConfirmationModal>
      <KeywordRuleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        rule={selected}
        onSave={(data) => dispatch({ type: "UPDATE", payload: data })}
      />
      <MainHeader>
        <Title>Auto-resposta por palavra-chave</Title>
        <MainHeaderButtonsWrapper>
          <Button variant="contained" color="primary" startIcon={<Add />} onClick={handleNew}>
            Nova regra
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>
      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Palavra-chave</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Resposta automática</TableCell>
              <TableCell align="center">Ativo</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRowSkeleton columns={5} />
            ) : rules.map(rule => (
              <TableRow key={rule.id} hover>
                <TableCell>
                  <Chip label={rule.keyword} size="small" variant="outlined" />
                </TableCell>
                <TableCell>{MATCH_LABELS[rule.matchType] || rule.matchType}</TableCell>
                <TableCell style={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {rule.response}
                </TableCell>
                <TableCell align="center">
                  <Switch
                    size="small"
                    checked={rule.isActive}
                    onChange={() => handleToggle(rule)}
                    color="primary"
                  />
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Editar">
                    <IconButton size="small" onClick={() => handleEdit(rule)}><Edit fontSize="small" /></IconButton>
                  </Tooltip>
                  <Tooltip title="Excluir">
                    <IconButton size="small" onClick={() => { setDeleteId(rule.id); setConfirmOpen(true); }}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {!loading && rules.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center" style={{ color: "gray", padding: 24 }}>
                  Nenhuma regra cadastrada. Crie a primeira clicando em "Nova regra".
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default KeywordRules;
